import { z } from "zod";

/**
 * Notification preference DTOs (CREW-599)
 * Shared types between backend and frontend for the per-user notification settings.
 *
 * A preference "kind" is what the user switches in the settings dialog. One kind can cover several
 * notification names in ngft-api (for example `course_reminder` covers the buffer-zone, 10-day and
 * 1-day reminders). Each kind has an email and an in-app channel; both false means off.
 */

// ============= Enums =============

export const NotificationKindSchema = z.enum([
  "course_reminder",
  "courses_assigned",
  "dg_appointment",
  "certificate_declined",
  "crew_course_reminder",
  "certificate_pending",
  "subscription_expiring",
]);

/** `mine`: about the user's own trainings. `crew`: about the people the user is responsible for. */
export const NotificationSectionSchema = z.enum(["mine", "crew"]);

/**
 * Who a kind can reach. `crew_member`: any member of a crew. `crew_training_responsible`: an NPCT or
 * a Deputy NPCT in at least one crew.
 */
export const NotificationAudienceSchema = z.enum(["crew_member", "crew_training_responsible"]);

// ============= Catalogue =============

export const NotificationKindInfoSchema = z.object({
  kind: NotificationKindSchema,
  section: NotificationSectionSchema,
  audience: NotificationAudienceSchema,
});

/**
 * Section and audience per kind. Typed as a `Record` over `NotificationKind`, so a kind added to
 * `NotificationKindSchema` without an entry here does not compile.
 */
const NOTIFICATION_KIND_DETAILS: Record<NotificationKind, Omit<NotificationKindInfo, "kind">> = {
  course_reminder: { section: "mine", audience: "crew_member" },
  courses_assigned: { section: "mine", audience: "crew_member" },
  dg_appointment: { section: "mine", audience: "crew_member" },
  certificate_declined: { section: "mine", audience: "crew_member" },
  crew_course_reminder: { section: "crew", audience: "crew_training_responsible" },
  certificate_pending: { section: "crew", audience: "crew_training_responsible" },
  subscription_expiring: { section: "crew", audience: "crew_training_responsible" },
};

/**
 * Every preference kind with its section and audience, in display order (the order of
 * `NotificationKindSchema`). The API uses it to decide which kinds are relevant to a user; the
 * dashboard uses it to lay out the settings dialog.
 */
export const NOTIFICATION_KINDS: readonly NotificationKindInfo[] = NotificationKindSchema.options.map((kind) => ({
  kind,
  ...NOTIFICATION_KIND_DETAILS[kind],
}));

// ============= Response Schemas =============

export const NotificationPreferenceSchema = z.object({
  kind: NotificationKindSchema,
  email: z.boolean(),
  inApp: z.boolean(),
});

/** The kinds relevant to the caller only, each with the stored value or the caller's default. */
export const GetNotificationPreferencesResponseSchema = z.object({
  preferences: z.array(NotificationPreferenceSchema),
});

// ============= Body Schemas =============

/** Upserts the listed kinds; kinds not listed keep their value. A kind not relevant to the caller is a 400. */
export const UpdateNotificationPreferencesRequestSchema = z.object({
  preferences: z
    .array(NotificationPreferenceSchema)
    .min(1)
    .refine((items) => new Set(items.map((item) => item.kind)).size === items.length, {
      message: "Each kind may appear only once",
    }),
});

// ============= Type Exports =============

export type NotificationKind = z.infer<typeof NotificationKindSchema>;
export type NotificationSection = z.infer<typeof NotificationSectionSchema>;
export type NotificationAudience = z.infer<typeof NotificationAudienceSchema>;
export type NotificationKindInfo = z.infer<typeof NotificationKindInfoSchema>;
export type NotificationPreference = z.infer<typeof NotificationPreferenceSchema>;
export type GetNotificationPreferencesResponse = z.infer<typeof GetNotificationPreferencesResponseSchema>;
export type UpdateNotificationPreferencesRequest = z.infer<typeof UpdateNotificationPreferencesRequestSchema>;
